import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRippleModule } from '@angular/material/core';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FavoritePage, FavoritesService } from '../../../../core/services/favorites.service';

@Component({
  selector: 'app-favorites-menu',
  standalone: true,
  imports: [
    CommonModule,
    MatMenuModule,
    MatIconModule,
    MatBadgeModule,
    MatButtonModule,
    MatTooltipModule,
    MatRippleModule,
    RouterModule,
    FormsModule,
    TranslateModule
  ],
  template: `
    <button
      mat-mini-fab
      color="inherit"
      class="d-flex justify-content-center"
      [matTooltip]="'FAVORITES.TOOLTIP' | translate"
      [matMenuTriggerFor]="favoritesMenu"
      [ngClass]="{'has-favorites': favorites.length > 0}"
    >
      <span [matBadge]="favorites.length" matBadgeSize="small" [matBadgeHidden]="favorites.length === 0" matBadgeColor="accent">
        <i class="iconify icon-22 d-flex" [attr.data-icon]="favorites.length > 0 ? 'ph:star-fill' : 'ph:star'"></i>
      </span>
    </button>

    <mat-menu #favoritesMenu="matMenu" class="favorites-menu-panel" (closed)="searchTerm = ''">
      <!-- Header -->
      <div class="fav-header" (click)="$event.stopPropagation()">
        <div class="fav-header-row">
          <div class="fav-header-title">
            <mat-icon class="fav-title-icon">star</mat-icon>
            <h3>{{ 'FAVORITES.TITLE' | translate }}</h3>
          </div>
          @if (favorites.length > 0) {
            <button
              mat-icon-button
              class="clear-all-btn"
              (click)="clearAll($event)"
              [matTooltip]="'FAVORITES.CLEAR_ALL' | translate"
            >
              <mat-icon>delete_sweep</mat-icon>
            </button>
          }
        </div>
        @if (favorites.length > 3) {
          <div class="fav-search-box">
            <mat-icon class="search-icon">search</mat-icon>
            <input
              type="text"
              [(ngModel)]="searchTerm"
              [placeholder]="'FAVORITES.SEARCH_PLACEHOLDER' | translate"
              class="fav-search-input"
              (click)="$event.stopPropagation()"
            />
            @if (searchTerm) {
              <button mat-icon-button class="clear-search-btn" (click)="searchTerm = ''; $event.stopPropagation()">
                <mat-icon>close</mat-icon>
              </button>
            }
          </div>
        }
      </div>

      <!-- Empty State -->
      @if (favorites.length === 0) {
        <div class="fav-empty-state" (click)="$event.stopPropagation()">
          <mat-icon class="empty-icon">star_border</mat-icon>
          <p class="empty-title">{{ 'FAVORITES.NO_FAVORITES' | translate }}</p>
          <p class="empty-hint">{{ 'FAVORITES.EMPTY_HINT' | translate }}</p>
        </div>
      }

      <!-- Favorites List -->
      @if (favorites.length > 0) {
        <div class="fav-list">
          @for (favorite of filteredFavorites; track favorite.id) {
            <button
              mat-menu-item
              class="fav-item"
              (click)="navigateTo(favorite.route)"
            >
              <div class="fav-item-icon-wrap">
                @if (favorite.icon) {
                  <i class="iconify fav-item-icon" [attr.data-icon]="favorite.icon"></i>
                } @else {
                  <mat-icon class="fav-item-icon">bookmark</mat-icon>
                }
              </div>
              <div class="fav-item-content">
                <span class="fav-item-title">{{ favorite.title }}</span>
                <span class="fav-item-route">{{ favorite.route }}</span>
              </div>
              <button
                mat-icon-button
                class="fav-remove-btn"
                (click)="removeFavorite($event, favorite.id)"
                [matTooltip]="'FAVORITES.REMOVE' | translate"
              >
                <mat-icon>close</mat-icon>
              </button>
            </button>
          }
          @if (filteredFavorites.length === 0 && searchTerm) {
            <div class="fav-no-results" (click)="$event.stopPropagation()">
              <mat-icon>search_off</mat-icon>
              <p>{{ 'FAVORITES.NO_RESULTS' | translate }}</p>
            </div>
          }
        </div>
      }

      <!-- Footer -->
      @if (favorites.length > 0) {
        <div class="fav-footer" (click)="$event.stopPropagation()">
          <span class="fav-count">{{ favorites.length }} {{ 'FAVORITES.PAGES' | translate }}</span>
        </div>
      }
    </mat-menu>
  `,
  styles: [`
    :host {
      display: inline-flex;
    }

    .has-favorites .iconify {
      color: #FFD700;
      filter: drop-shadow(0 0 2px rgba(255, 215, 0, 0.4));
    }

    .fav-header {
      padding: 14px 16px 10px;
      border-bottom: 1px solid var(--gray1, rgba(0, 0, 0, 0.08));
    }

    .fav-header-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 4px;
    }

    .fav-header-title {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .fav-title-icon {
      color: #FFD700;
      font-size: 22px;
      width: 22px;
      height: 22px;
    }

    .fav-header h3 {
      margin: 0;
      font-size: 15px;
      font-weight: 600;
      color: var(--mat-theme-primary-color);
    }

    .clear-all-btn {
      width: 32px;
      height: 32px;
      line-height: 32px;
    }

    .clear-all-btn mat-icon {
      font-size: 18px;
      color: var(--muted, rgba(0, 0, 0, 0.45));
    }

    .clear-all-btn:hover mat-icon {
      color: var(--error, #ff6692);
    }

    .fav-search-box {
      display: flex;
      align-items: center;
      background: var(--gray-200, rgba(0, 0, 0, 0.04));
      border-radius: 8px;
      padding: 4px 10px;
      margin-top: 8px;
      transition: background 0.2s;
    }

    .fav-search-box:focus-within {
      background: var(--gray-200, rgba(0, 0, 0, 0.06));
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--mat-theme-primary-color) 20%, transparent);
    }

    .search-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
      color: var(--muted, rgba(0, 0, 0, 0.4));
      margin-inline-end: 8px;
    }

    .fav-search-input {
      border: none;
      outline: none;
      background: transparent;
      font-size: 13px;
      flex: 1;
      padding: 6px 0;
      font-family: inherit;
      color: inherit;
    }

    .clear-search-btn {
      width: 24px;
      height: 24px;
      line-height: 24px;
    }

    .clear-search-btn mat-icon {
      font-size: 14px;
      width: 14px;
      height: 14px;
    }

    .fav-empty-state {
      padding: 30px 20px;
      text-align: center;
    }

    .empty-icon {
      font-size: 48px;
      width: 48px;
      height: 48px;
      color: var(--gray-400, rgba(0, 0, 0, 0.15));
      margin-bottom: 12px;
    }

    .empty-title {
      margin: 0 0 6px;
      font-size: 14px;
      font-weight: 500;
      color: var(--bodyColor, rgba(0, 0, 0, 0.55));
    }

    .empty-hint {
      margin: 0;
      font-size: 12px;
      color: var(--muted, rgba(0, 0, 0, 0.35));
    }

    .fav-list {
      max-height: 340px;
      overflow-y: auto;
      padding: 4px 0;
    }

    .fav-item {
      display: flex !important;
      align-items: center !important;
      padding: 8px 16px !important;
      padding-inline-end: 44px !important;
      min-height: 52px !important;
      position: relative;
      transition: background 0.15s;
    }

    .fav-item-icon-wrap {
      width: 34px;
      height: 34px;
      border-radius: 8px;
      background: color-mix(in srgb, var(--mat-theme-primary-color) 10%, transparent);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-inline-end: 12px;
      flex-shrink: 0;
    }

    .fav-item-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
      color: var(--mat-theme-primary-color);
    }

    .fav-item-content {
      display: flex;
      flex-direction: column;
      overflow: hidden;
      flex: 1;
    }

    .fav-item-title {
      font-size: 13px;
      font-weight: 500;
      color: var(--textPrimary, rgba(0, 0, 0, 0.85));
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .fav-item-route {
      font-size: 11px;
      color: var(--muted, rgba(0, 0, 0, 0.35));
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-top: 2px;
    }

    .fav-remove-btn {
      position: absolute;
      inset-inline-end: 8px;
      top: 50%;
      transform: translateY(-50%);
      width: 28px;
      height: 28px;
      line-height: 28px;
      opacity: 0;
      transition: opacity 0.15s;
    }

    .fav-item:hover .fav-remove-btn {
      opacity: 1;
    }

    .fav-remove-btn mat-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
      line-height: 16px;
      color: var(--muted, rgba(0, 0, 0, 0.4));
    }

    .fav-remove-btn:hover mat-icon {
      color: var(--error, #ff6692);
    }

    .fav-no-results {
      padding: 24px 16px;
      text-align: center;
      color: var(--muted, rgba(0, 0, 0, 0.4));
    }

    .fav-no-results mat-icon {
      font-size: 32px;
      width: 32px;
      height: 32px;
      margin-bottom: 8px;
      display: block;
      margin-inline: auto;
    }

    .fav-no-results p {
      margin: 0;
      font-size: 13px;
    }

    .fav-footer {
      padding: 8px 16px;
      border-top: 1px solid var(--gray1, rgba(0, 0, 0, 0.06));
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .fav-count {
      font-size: 11px;
      color: var(--muted, rgba(0, 0, 0, 0.4));
      font-weight: 500;
    }
  `]
})
export class FavoritesMenuComponent implements OnInit {
  favorites: FavoritePage[] = [];
  searchTerm = '';
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;

  constructor(
    private favoritesService: FavoritesService,
    private router: Router,
    public translate: TranslateService
  ) { }

  ngOnInit(): void {
    this.favoritesService.favorites$.subscribe(favorites => {
      this.favorites = favorites.sort((a, b) => a.title.localeCompare(b.title));
    });
  }

  get filteredFavorites(): FavoritePage[] {
    if (!this.searchTerm.trim()) {
      return this.favorites;
    }
    const term = this.searchTerm.toLowerCase();
    return this.favorites.filter(f =>
      f.title.toLowerCase().includes(term) || f.route.toLowerCase().includes(term)
    );
  }

  removeFavorite(event: Event, id: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.favoritesService.removeFavorite(id);
  }

  clearAll(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const all = [...this.favorites];
    all.forEach(f => this.favoritesService.removeFavorite(f.id));
  }

  navigateTo(route: string): void {
    this.router.navigateByUrl(route);
  }

  openMenu(): void {
    if (this.menuTrigger) {
      this.menuTrigger.openMenu();
    }
  }
}
